// frontend/src/components/canvasser/calls/CallOutcomeSheet.tsx
import { useState } from 'react'
import { Alert, Button, Checkbox, CloseButton, Drawer, Field, Portal, SimpleGrid, Text, Textarea, VStack } from '@chakra-ui/react'
import { CALL_OUTCOME_LABELS, type CallOutcome } from '../../../types/followUp'

const OUTCOMES = Object.keys(CALL_OUTCOME_LABELS) as CallOutcome[]
/** Input length only; the server validates the note itself (and owns the real cap). */
const NOTE_INPUT_MAX = 500
const DNC_OUTCOMES: ReadonlySet<CallOutcome> = new Set<CallOutcome>(['Talked', 'Refused'])

export interface CallOutcomeChoice { outcome: CallOutcome; note: string; markDoNotContact: boolean }

/**
 * [CF-1 P4 §7.6] How did the call go? The eight outcomes, an optional note, and "Asked not to be called
 * again" (Talked / Refused only). Stays open while saving and after a failed save, so a retry reuses the
 * same clientCallId the parent minted when the sheet opened.
 */
export function CallOutcomeSheet({ open, name, notice, saving, onClose, onSubmit }: {
  open: boolean; name: string; notice: string | null; saving: boolean
  onClose: () => void; onSubmit: (c: CallOutcomeChoice) => void
}) {
  const [outcome, setOutcome] = useState<CallOutcome | null>(null)
  const [note, setNote] = useState('')
  const [dnc, setDnc] = useState(false)
  const reset = () => { setOutcome(null); setNote(''); setDnc(false) }
  const dncOffered = outcome !== null && DNC_OUTCOMES.has(outcome)
  return (
    <Drawer.Root open={open} placement="bottom" onOpenChange={(e) => { if (!e.open) { reset(); onClose() } }}>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" pb="env(safe-area-inset-bottom, 0px)" shadow="popover" maxH="90dvh">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" minW="44px" minH="44px" /></Drawer.CloseTrigger>
            <Drawer.Header>
              <VStack align="start" gap={0}>
                <Drawer.Title>How did the call go?</Drawer.Title>
                <Text fontSize="sm" color="fg.muted">{name}</Text>
              </VStack>
            </Drawer.Header>
            <Drawer.Body>
              <VStack align="stretch" gap={4} pb={4}>
                {notice && (
                  <Alert.Root status="info" borderRadius="md"><Alert.Indicator />{notice}</Alert.Root>
                )}
                <SimpleGrid columns={2} gap={2}>
                  {OUTCOMES.map(o => (
                    <Button key={o} data-outcome={o} minH="48px" variant="outline" aria-pressed={outcome === o}
                      borderColor={outcome === o ? 'accent' : 'border'} bg={outcome === o ? 'accent.soft' : 'surface'}
                      color={outcome === o ? 'accent' : 'fg'} whiteSpace="normal"
                      onClick={() => { setOutcome(o); if (!DNC_OUTCOMES.has(o)) setDnc(false) }}>
                      {CALL_OUTCOME_LABELS[o]}
                    </Button>
                  ))}
                </SimpleGrid>
                {dncOffered && (
                  <Checkbox.Root checked={dnc} onCheckedChange={(e) => setDnc(!!e.checked)} minH="44px">
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>Asked not to be called again</Checkbox.Label>
                  </Checkbox.Root>
                )}
                <Field.Root>
                  <Field.Label>Note (optional)</Field.Label>
                  <Textarea value={note} maxLength={NOTE_INPUT_MAX} rows={2} onChange={(e) => setNote(e.target.value)} />
                </Field.Root>
                <Button minH="52px" colorPalette="brand" disabled={!outcome || saving} loading={saving}
                  onClick={() => { if (outcome) onSubmit({ outcome, note: note.trim(), markDoNotContact: dncOffered && dnc }) }}>
                  Save
                </Button>
              </VStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
